import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, Handler, HasOnError, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import translate from "@insite/client-framework/Translate";
import { ProductAutocompleteItemModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import loadProductsByProductNumbers from "@store/Store/Data/Products/Handlers/LoadProductsByProductNumbers";
import addProductFromQuote from "@store/Store/Data/QuoteToQuickOrder/Handlers/AddProductFromQuote";
import { StoreHandlerProps } from "@store/Store/HandlerCreator";

interface ProductLine {
    productNumber?: string;
    quantity?: number;
    raw?: string;
    formatValidation?: string;
    result?: ProductAutocompleteItemModel;
    resultError?: string;
}

export interface GetBulkQuickOrderApiParameter {
    productLines: ProductLine[];
}
export interface QuoteLineToCartModel {
    productId: string;
    quantityOrdered: number;
    unitOfMeasure: string;
    productInfo: ProductInfo | undefined;
}
export interface GetBulkQuickOrderApiResult {
    message: string;
    productAllLoaded: boolean;
    productIds?: string[];
    nbTotal?: number;
    nbAdded?: number;
}

type Parameter = {
    apiParameter: GetBulkQuickOrderApiParameter;
    onSegmentComplete?: (step:string, nbComplete?:number, nbProcessed?:number, nbTotal?:number) => void;
} & HasOnSuccess<GetBulkQuickOrderApiResult> &
    HasOnError<any>;
type Props = StoreHandlerProps & {
    apiParameter: GetBulkQuickOrderApiParameter;
    apiResult: GetBulkQuickOrderApiResult;
};

type HandlerType = Handler<Parameter, Props>;

export const DispatchBegiBulkAddToQuickOrder: HandlerType = props => {
    props.apiResult = {
        message: "",
        productAllLoaded: false,
    };

    props.dispatch({
        type: "Store/Data/QuickOrder/BeginLoadProducts",
        parameter: props.apiParameter,
    });
};
export const PopulateApiParameter: HandlerType = props => {
    const { onError, ...parameter } = props.parameter;
    props.apiParameter = parameter.apiParameter;
};


export const SearchProductsQuickOrder: HandlerType = async props => {
    let errorMessage = "";
    const size = 24;
    let allProductsAdded = false;
    const productNotFound:string[] = [];
    let allProductsFound:ProductModel[] = [];

    props.parameter.onSegmentComplete?.("ImportQuote_ProductsLoading");
    
    for (let i = 0; i < props.apiParameter.productLines.length; i += size) {
        let loadedProducts = false;
        const productLinesChunk = props.apiParameter.productLines.slice(i, i + size);
        let products:ProductModel[] | null = [];

        const productNumbers:string[] = productLinesChunk.map(p => p.productNumber).filter(pn => !!pn) as string[];

        if(!productNumbers) {
            break;
        }

        props.dispatch(
            loadProductsByProductNumbers({
                productNumbers,
                pageSize: productNumbers.length,
                onComplete: (result) => {
                    products = result.apiResult?.products || null;
                    if (products) {
                        allProductsFound = allProductsFound.concat(products);
                    }
                    loadedProducts = true;
                },
            }),
        );
        await waitFor(() => loadedProducts === true);
    }

    const productIds:string[] = allProductsFound.map(p => p.id);

    const bulkProductQuoteLines:QuoteLineToCartModel[] = props.apiParameter.productLines.map((productLine) => {
        const product = allProductsFound?.find(p => {
            return p.properties.fullErpNumber === productLine.productNumber || p.productNumber === productLine.productNumber;
        });
        const result = !!product;
        if (!result && productLine.productNumber) {
            productNotFound.push(productLine.productNumber);
            const line = translate(`No valid result for {0}`, `${productLine.productNumber}`);
            errorMessage += `${(errorMessage===""?"":"<br/>")}${line}`;
        }
        if (product) {
            const productInfo: ProductInfo = {
                productId: product.id,
                qtyOrdered: productLine.quantity !== undefined ? productLine.quantity : 1,
                unitOfMeasure: "",
                productDetailPath: "",
            };
            return {
                productId: product.id,
                quantityOrdered: productLine.quantity || 1,
                unitOfMeasure: product?.unitOfMeasures?.[0].unitOfMeasure || "",
                productInfo,
            };
        }
        return null;
    }).filter(o => !!o) as QuoteLineToCartModel[];

    if (errorMessage) {
        props.apiResult.message = errorMessage;
    }
    
    props.dispatch(addProductFromQuote({
        productIds,
        quoteLines: bulkProductQuoteLines,
        onComplete: () => {
            allProductsAdded = true;
        },
        onSegmentComplete: (step, nbComplete, nbProcessed, nbTotal) => {
            props.parameter.onSegmentComplete?.(step, nbComplete, nbProcessed, nbTotal);
        },
    }));

    await waitFor(() => allProductsAdded === true);
    
    props.apiResult.productAllLoaded = true;
    props.apiResult.nbTotal = props.apiParameter.productLines.length;
    props.apiResult.nbAdded = props.apiResult.nbTotal - productNotFound.length;
};

export const DispatchCompleteBulkAddToQuickOrder: HandlerType = props => {
    props.dispatch({
        type: "Store/Data/QuickOrder/CompleteLoadProducts",
        parameter: props.apiParameter,
    });
};

export const chain = [
    PopulateApiParameter,
    DispatchBegiBulkAddToQuickOrder,
    SearchProductsQuickOrder,
    DispatchCompleteBulkAddToQuickOrder,
];

const bulkAddToQuickOrder = createHandlerChainRunner(chain, "BulkAddToQuickOrder");
export default bulkAddToQuickOrder;
